import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { SidePopUpComponent, SvgComponent } from '@app/base-components';
import { PopupService } from '@app/services';
import { DataLayerService, DlEventName } from 'haba-base-components';
import {
  ActiveCartService,
  BaseSiteService,
  I18nModule,
  LanguageService,
  RoutingService,
} from 'haba-core';
import { PrefixboxService, ProductHit } from 'haba-emporix';
import { combineLatest, of, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { ProductCardInlineComponent } from '../../product/product-card-inline/product-card-inline.component';

@Component({
  selector: 'app-add-to-cart-popup',
  templateUrl: './add-to-cart-popup.component.html',
  styleUrls: ['./add-to-cart-popup.component.scss'],
  imports: [
    NgIf,
    AsyncPipe,
    I18nModule,
    SidePopUpComponent,
    SvgComponent,
    ProductCardInlineComponent,
  ],
})
export class AddToCartPopupComponent implements OnInit, OnDestroy {
  product$ = this.popupService.product$;
  quantity$ = this.popupService.quantity$;
  personalizationImage$ = this.popupService.personalizationImage$;
  recommendations: ProductHit[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    public popupService: PopupService,
    private prefixboxService: PrefixboxService,
    private activeCartService: ActiveCartService,
    private baseSiteService: BaseSiteService,
    private languageService: LanguageService,
    private dataLayerService: DataLayerService,
    private routingService: RoutingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.popupService.product$.pipe(takeUntil(this.destroy$)).subscribe((product) => {
      this.recommendations = [];
      this.cdr.detectChanges();
      if (product) {
        this.loadRecommendations(product);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateCart(): void {
    this.popupService.close();
    this.routingService.go({ cxRoute: 'cart' });
  }

  addRecommendationToCart(product: ProductHit): void {
    combineLatest([this.languageService.getActive(), this.baseSiteService.getActive()])
      .pipe(
        take(1),
        switchMap(([language, siteCode]) => {
          const minimalProduct = this.buildMinimalProduct(product, language, siteCode);
          this.activeCartService.addEntry(minimalProduct, 1);
          return combineLatest([
            this.activeCartService.getEntries(),
            this.activeCartService.isStable(),
          ]);
        }),
        filter(([, isStable]) => isStable),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.dataLayerService.setDataLayerFromEvent(DlEventName.ADD_TO_CART, {
          productCode: product.code,
        });
        this.prefixboxService.trackRecommendationAddToCart(product, 'personalized');
      });
  }

  private loadRecommendations(product: ProductHit): void {
    this.prefixboxService
      .getInternalMatchingProductsRecommendations(product.code)
      .pipe(
        take(1),
        switchMap((products) =>
          products?.length
            ? of(products)
            : this.prefixboxService.getPersonalizedRecommendations(product).pipe(take(1))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((products) => {
        this.recommendations = products ?? [];
        this.cdr.detectChanges();
      });
  }

  private buildMinimalProduct(product: ProductHit, language: string, siteCode: string) {
    const raw = product as any;
    return {
      id: raw.id,
      sku: product.code,
      language,
      siteCode,
      name: product.name,
      yrn: raw.yrn,
      price: typeof raw.price === 'object' ? raw.price : {},
      mixins: raw.mixins
        ? {
            'haba-product-prices': raw.mixins['haba-product-prices'],
            'haba-product-availabilities': raw.mixins['haba-product-availabilities'],
            'haba-product-sap-data': raw.mixins['haba-product-sap-data'],
            'haba-product-medias': raw.mixins['haba-product-medias'],
            'haba-product-category-data': raw.mixins['haba-product-category-data'],
          }
        : {},
    };
  }
}
